<div class="shell">
  @if (showShell()) {
  <!-- Skip link: first focusable element; visible only while focused. Handled in code rather
       than as a hash jump so the Router doesn't re-run the route on the fragment change. -->
  <a class="skip-link" href="#main" (click)="skipToMain($event)">{{ 'shell.skipToMain' | translate }}</a>

  <!-- Tier 1: Navy top bar -->
  <header class="topbar">
    <div class="topbar-left">
      <button class="hamburger-btn icon-btn" (click)="$event.stopPropagation(); openMobileDrawer()" [attr.aria-label]="'topbar.menu' | translate">
        <mat-icon>menu</mat-icon>
      </button>
      <img class="logo" src="assets/logo/hilbertech-extranet-white.svg" height="26" alt="Hilbertech Extranet" />
    </div>
    <div class="topbar-right">
      @if (isAdmin) {
        <!-- Platform Admin badge — replaces the property selector, which has nothing to select
             for an admin (no owned properties). Matches the prototype's Shell.jsx isAdmin branch. -->
        <div class="admin-badge">
          <mat-icon class="prop-icon">shield</mat-icon>
          <span>{{ 'topbar.platformAdmin' | translate }}</span>
        </div>
      } @else if (showPropertyChrome()) {
      <!-- Property selector — hotel managers only: an agency works from contracts, not "inside"
           one property (handoff R9), so it never gets a property switcher. -->
      <div class="prop-selector">
        <div class="prop-selector-content" (click)="togglePropertyDropdown($event)">
          <mat-icon class="prop-icon">apartment</mat-icon>
          <div class="prop-info">
            @if (selectedProperty(); as sel) {
              <span class="prop-name">{{ sel.name }}</span>
              <span class="prop-city">{{ sel.city }}</span>
            } @else {
              <span class="prop-name">{{ 'topbar.noPropertiesYet' | translate }}</span>
            }
          </div>
          <mat-icon class="chevron">{{ propertyDropdownOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
        </div>
        @if (propertyDropdownOpen()) {
          <div class="dropdown prop-dropdown" (click)="$event.stopPropagation()">
            @if (properties().length) {
              @for (prop of properties(); track prop.id) {
                <div class="dropdown-item" [class.active]="prop.id === selectedProperty()?.id" (click)="selectProperty(prop, $event)">
                  <span class="prop-thumb" [style.backgroundImage]="'url(' + propPhoto(prop) + ')'"></span>
                  <span class="prop-item-info">
                    <span class="dropdown-item-name">{{ prop.name }}</span>
                    <span class="dropdown-item-sub">{{ prop.city }}, {{ prop.country }}</span>
                  </span>
                  @if (prop.id === selectedProperty()?.id) {
                    <mat-icon class="prop-check">check</mat-icon>
                  }
                </div>
              }
            } @else {
              <div class="dropdown-item" style="cursor:default">{{ 'topbar.noPropertiesYet' | translate }}</div>
            }
            <a class="prop-manage-all" routerLink="/extranet/properties" (click)="propertyDropdownOpen.set(false)">
              <mat-icon>grid_view</mat-icon> {{ 'topbar.manageAllProperties' | translate }}
            </a>
          </div>
        }
      </div>
      }
      <!-- Bell -->
      <div class="bell-wrap">
        <button class="icon-btn" [attr.aria-label]="bellAriaLabel()" (click)="toggleBellDropdown($event)">
          <mat-icon>notifications</mat-icon>
          @if (unreadCount > 0) {
            <span class="bell-badge">{{ unreadCount }}</span>
          }
        </button>
        @if (bellDropdownOpen()) {
          <div class="dropdown bell-dropdown" (click)="$event.stopPropagation()">
            <div class="bell-header">
              <span class="bell-title">{{ 'topbar.notifications.title' | translate }}</span>
              <button class="bell-mark-all" (click)="markAllRead($event)">{{ 'topbar.notifications.markAllRead' | translate }}</button>
            </div>
            <div class="bell-list">
              @for (n of recentNotifications; track n.id) {
                <div class="bell-item" [class.unread]="!n.read">
                  <span class="bell-dot" [style.background]="n.color"></span>
                  <div class="bell-item-content">
                    <div class="bell-item-title">{{ n.title }}</div>
                    <div class="bell-item-body">{{ n.body }}</div>
                    <div class="bell-item-time">{{ n.time }}</div>
                  </div>
                </div>
              } @empty {
                <div class="bell-empty">{{ 'topbar.notifications.empty' | translate }}</div>
              }
            </div>
            <a class="bell-view-all" routerLink="/extranet/notifications" (click)="bellDropdownOpen.set(false)">{{ 'topbar.notifications.viewAll' | translate }}</a>
          </div>
        }
      </div>
      <!-- No Help (?) button and no "Help center" menu item (WI 3829): there is no help destination
           yet, and a control that does nothing is not shown. The topbar.help and
           topbar.account.helpCenter strings stay for when one exists. -->
      <!-- Account -->
      <div class="account-btn">
        <div class="account-trigger" (click)="toggleAccountDropdown($event)">
          <div class="avatar">{{ initials(identitySvc.displayName()) }}</div>
          <mat-icon class="chevron">{{ accountDropdownOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
        </div>
        @if (accountDropdownOpen()) {
          <div class="dropdown account-dropdown" (click)="$event.stopPropagation()">
            <div class="dropdown-header">
              <div class="avatar">{{ initials(identitySvc.displayName()) }}</div>
              <div>
                <div style="font-weight:600;color:var(--ext-text-default)">{{ identitySvc.displayName() }}</div>
                <div style="font-size:12px;color:var(--ext-text-muted)">{{ identitySvc.me()?.email }}</div>
                <div class="role-badge">{{ roleBadgeText() }}</div>
              </div>
            </div>
            @if (identitySvc.canSwitchRole()) {
              <div class="dropdown-divider"></div>
              <div class="view-as">
                <div class="view-as-label">
                  <mat-icon>sync_alt</mat-icon>
                  <span>{{ 'topbar.viewAs.label' | translate }}</span>
                  <span class="view-as-hint">{{ 'topbar.viewAs.devOnlyHint' | translate }}</span>
                </div>
                <div class="view-as-options">
                  @for (r of identitySvc.availableRoles(); track r) {
                    <button type="button"
                            class="view-as-option"
                            [class.active]="r === identitySvc.activeRole()"
                            (click)="switchRole(r, $event)">
                      {{ roleLabel(r) }}
                      @if (r === identitySvc.activeRole()) { <mat-icon>check</mat-icon> }
                    </button>
                  }
                </div>
              </div>
            }
            <div class="dropdown-divider"></div>
            @if (!isAdmin) {
              <a class="dropdown-item" routerLink="/extranet/settings" (click)="accountDropdownOpen.set(false)">
                <mat-icon>settings</mat-icon> {{ 'topbar.account.settings' | translate }}
              </a>
              <!-- The Owner of a group only (Feature 4123): inviting is never a configurable area,
                   and a legacy hotel account (Owner without a group) has no team to invite into. -->
              @if (identitySvc.canManageTeam()) {
                <a class="dropdown-item" routerLink="/extranet/team/invite" (click)="accountDropdownOpen.set(false)">
                  <mat-icon>person_add</mat-icon> {{ 'topbar.account.inviteTeammate' | translate }}
                </a>
              }
            }
            <!-- Hidden while an admin views the product as a group (F7): the admin area is one
                 click away in the banner, and this link would only bounce the viewed role home. -->
            @if (isAgencyManager && !identitySvc.isViewingAs()) {
              <a class="dropdown-item" routerLink="/extranet/admin" (click)="accountDropdownOpen.set(false)">
                <mat-icon>manage_accounts</mat-icon> {{ 'topbar.account.userManagement' | translate }}
              </a>
            }
            <div class="dropdown-divider"></div>
            <div class="dropdown-item danger" (click)="signOut()">
              <mat-icon class="ext-rtl-flip">logout</mat-icon> {{ 'topbar.account.signOut' | translate }}
            </div>
          </div>
        }
      </div>
    </div>
  </header>

  <!-- Mobile: property selector strip (below the top bar) — hotel managers only: nothing for an
       admin to select (no owned properties) and no property context for an agency (R9). -->
  @if (showPropertyChrome()) {
  <div class="prop-strip">
    <div class="prop-selector prop-selector--full">
      <div class="prop-selector-content" (click)="togglePropertyDropdown($event)">
        <mat-icon class="prop-icon">apartment</mat-icon>
        <div class="prop-info">
          @if (selectedProperty(); as sel) {
            <span class="prop-name">{{ sel.name }}</span>
            <span class="prop-city">{{ sel.city }}</span>
          } @else {
            <span class="prop-name">{{ 'topbar.noPropertiesYet' | translate }}</span>
          }
        </div>
        <mat-icon class="chevron">{{ propertyDropdownOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </div>
      @if (propertyDropdownOpen()) {
        <div class="dropdown prop-dropdown" (click)="$event.stopPropagation()">
          @if (properties().length) {
            @for (prop of properties(); track prop.id) {
              <div class="dropdown-item" [class.active]="prop.id === selectedProperty()?.id" (click)="selectProperty(prop, $event)">
                <span class="prop-thumb" [style.backgroundImage]="'url(' + propPhoto(prop) + ')'"></span>
                <span class="prop-item-info">
                  <span class="dropdown-item-name">{{ prop.name }}</span>
                  <span class="dropdown-item-sub">{{ prop.city }}, {{ prop.country }}</span>
                </span>
                @if (prop.id === selectedProperty()?.id) {
                  <mat-icon class="prop-check">check</mat-icon>
                }
              </div>
            }
          } @else {
            <div class="dropdown-item" style="cursor:default">{{ 'topbar.noPropertiesYet' | translate }}</div>
          }
          <a class="prop-manage-all" routerLink="/extranet/properties" (click)="propertyDropdownOpen.set(false)">
            <mat-icon>grid_view</mat-icon> {{ 'topbar.manageAllProperties' | translate }}
          </a>
        </div>
      }
    </div>
  </div>
  }

  <!-- Shell banner slot (ShellBannerService) — F7's admin "Viewing the product as …" bar plugs
       in here without touching the shell again; its buttons (secondary, then main) are the
       banner's own declarative actions. -->
  @if (shellBanner(); as banner) {
    <div class="shell-banner">
      <ext-banner [tone]="banner.tone" [icon]="banner.icon ?? ''" [actions]="shellBannerActions().buttons" (actionClick)="onShellBannerAction($event)">
        {{ banner.text }}
      </ext-banner>
    </div>
  }

  <!-- F9: the one confirmation before an admin's viewpoint starts writing for real. Lives in the
       shell because its trigger (the banner's "Enable editing") does. -->
  @if (viewAs.writeConfirmPending()) {
    <ext-view-as-write-dialog [groupName]="viewAsGroupName()" [adminEmail]="identitySvc.me()?.email ?? ''"
                              (confirmed)="viewAs.confirmWrite()" (closed)="viewAs.cancelWrite()" />
  }

  <!-- Tier 2: White tab nav (desktop) — a scrolling strip (never wraps, never overflows the page;
       handoff §19 "Shell nav"). Parents with children are two targets: the label navigates to the
       parent's own route, the chevron opens the submenu popover (a CDK overlay, portalled, so the
       strip's overflow clipping can't cut it). -->
  <nav #tabnav class="tabnav ext-scroll-x" [attr.aria-label]="'nav.ariaLabel' | translate">
    @for (item of navItems(); track item.route) {
      @if (item.children?.length) {
        <div class="tab-group" [class.active]="activeParents().has(item.route)" [class.open]="openMenu() === item.route">
          <a class="tab-item tab-item--parent" [routerLink]="item.route" routerLinkActive="active" [routerLinkActiveOptions]="subsetMatch">
            <mat-icon aria-hidden="true">{{ item.icon }}</mat-icon>
            <span>{{ item.label | translate }}</span>
          </a>
          <button type="button" class="tab-chevron" cdkOverlayOrigin #origin="cdkOverlayOrigin"
                  aria-haspopup="menu" [attr.aria-expanded]="openMenu() === item.route"
                  [attr.aria-controls]="openMenu() === item.route ? menuId(item) : null"
                  [attr.aria-label]="'nav.openSubmenu' | translate: { label: (item.label | translate) }"
                  (click)="toggleMenu(item, $event)" (keydown)="onTriggerKeydown($event, item)">
            <mat-icon aria-hidden="true">{{ openMenu() === item.route ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
          <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="origin" [cdkConnectedOverlayOpen]="openMenu() === item.route"
                       [cdkConnectedOverlayPositions]="menuPositions" [cdkConnectedOverlayViewportMargin]="8" [cdkConnectedOverlayPush]="true"
                       [cdkConnectedOverlayHasBackdrop]="true" cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                       (attach)="onMenuAttached()" (backdropClick)="closeMenu()" (detach)="closeMenu()">
            <div class="nav-menu" role="menu" [id]="menuId(item)" [attr.aria-label]="item.label | translate" (keydown)="onMenuKeydown($event)">
              @for (child of item.children; track child.route) {
                <a class="nav-menu-item" role="menuitem" tabindex="-1" [routerLink]="child.route"
                   routerLinkActive="active" #rla="routerLinkActive" [routerLinkActiveOptions]="child.exact ? exactMatch : subsetMatch"
                   [attr.aria-current]="rla.isActive ? 'page' : null" (click)="closeMenu()">
                  @if (child.icon) { <mat-icon class="nav-menu-icon" aria-hidden="true">{{ child.icon }}</mat-icon> }
                  <span class="nav-menu-label">{{ child.label | translate }}</span>
                  @if (rla.isActive) { <mat-icon class="nav-menu-check" aria-hidden="true">check</mat-icon> }
                </a>
              }
            </div>
          </ng-template>
        </div>
      } @else {
        <a class="tab-item" [routerLink]="item.route" routerLinkActive="active" [routerLinkActiveOptions]="exactMatch">
          <mat-icon aria-hidden="true">{{ item.icon }}</mat-icon>
          <span>{{ item.label | translate }}</span>
        </a>
      }
    }
  </nav>

  <!-- Mobile drawer: parents with children become a plain header row with their children indented
       under it (on a phone a nested popover is one more tap for nothing — Shell.jsx). -->
  @if (mobileDrawerOpen()) {
    <div class="mobile-drawer-overlay" (click)="closeMobileDrawer()">
      <div class="mobile-drawer" role="dialog" aria-modal="true" [attr.aria-label]="'topbar.menu' | translate" (click)="$event.stopPropagation()">
        <div class="mobile-drawer-header">
          <img class="logo" src="assets/logo/hilbertech-extranet-white.svg" height="24" alt="Hilbertech Extranet" />
          <button class="icon-btn" (click)="closeMobileDrawer()" [attr.aria-label]="'topbar.close' | translate">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <nav class="mobile-drawer-nav" [attr.aria-label]="'nav.ariaLabel' | translate">
          @for (item of navItems(); track item.route) {
            @if (item.children?.length) {
              <div class="mobile-drawer-group" role="group" [attr.aria-labelledby]="menuId(item) + '-m'">
                <div class="mobile-drawer-group-title" [id]="menuId(item) + '-m'">
                  <mat-icon aria-hidden="true">{{ item.icon }}</mat-icon>
                  <span>{{ item.label | translate }}</span>
                </div>
                @for (child of item.children; track child.route) {
                  <a class="mobile-drawer-item mobile-drawer-item--child" [routerLink]="child.route"
                     routerLinkActive="active" [routerLinkActiveOptions]="child.exact ? exactMatch : subsetMatch" (click)="closeMobileDrawer()">
                    @if (child.icon) { <mat-icon aria-hidden="true">{{ child.icon }}</mat-icon> }
                    <span>{{ child.label | translate }}</span>
                  </a>
                }
              </div>
            } @else {
              <a class="mobile-drawer-item" [routerLink]="item.route" routerLinkActive="active" [routerLinkActiveOptions]="exactMatch" (click)="closeMobileDrawer()">
                <mat-icon aria-hidden="true">{{ item.icon }}</mat-icon>
                <span>{{ item.label | translate }}</span>
              </a>
            }
          }
        </nav>
      </div>
    </div>
  }

  }

  <!-- Main content — the skip link's and route-change focus target -->
  <main #main id="main" class="main-content" tabindex="-1">
    <router-outlet />
  </main>
</div>
